import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="sim-cell"
//
// Lives on the inline inventory-balance simulation <form>. UI state only — all
// cascade math stays server-side. Responsibilities:
//   1. Click-to-edit: swap a cell's display value for an input-group (input + ✓/✗).
//   2. Commit model (Excel-like + explicit): Enter / ✓ / click-away / clicking
//      another cell commit (submit the form); Esc / ✗ discard the in-progress edit.
//   3. Focus restoration: each commit re-renders the Turbo frame and destroys the
//      DOM, so the "cell to open next" is stashed in sessionStorage and reopened
//      on reconnect — clicking another cell keeps the editing flow unbroken.
//   4. Per-row remove: drop an added (what-if) allocation row.
//   5. Scroll: preserve the table's horizontal scroll across re-renders.
//   6. Split sales cells: a sales editor holds one unnamed input per shipment
//      ([data-sim-cell-part]); before submit their non-blank values are joined
//      with "+" into the cell's single named field (sim[s][date][label]=300+200),
//      so the wire format and last-field-wins edits stay unchanged.
export default class extends Controller {
  static values = { key: String }

  connect() {
    this.openId = null
    this.submitting = false

    this.#restoreScroll()
    this.#reopenPendingCell()
  }

  disconnect() {
    this.scrollBindings?.forEach(({ scroller, handler }) => {
      scroller.removeEventListener("scroll", handler)
    })
  }

  // mousedown on a cell's display value — fires BEFORE the open input's blur, so
  // we can record the cell to focus after the imminent commit/re-render.
  arm(event) {
    if (!this.openId) return
    const id = event.currentTarget.dataset.simCellId
    if (id && id !== this.openId) {
      sessionStorage.setItem(this.#focusKey(), id)
    }
  }

  // click on a cell's display value.
  open(event) {
    const span = event.currentTarget
    const id = span.dataset.simCellId
    // If another editor is open, the blur handler will commit it; arm() already
    // recorded this cell so it reopens after the re-render.
    if (this.openId && this.openId !== id) return
    this.#reveal(span)
  }

  // Plan lens radios and other toolbar submits — just submit.
  submit() {
    this.#submitForm()
  }

  // Before Turbo serializes the GET form, make sales rows added while a crop tab
  // is active crop-aware. The server wire format stays label-based:
  // sim[rows][s][]=Client / Crop.
  prepareSubmit(event) {
    this.#scopeSelectedSalesRowsToActiveCrop()
    this.#focusFirstCellForAddedRow(event?.submitter)
  }

  // Enter or the ✓ button.
  commit(event) {
    event.preventDefault()
    this.#submitForm()
  }

  // "+ Add shipment" button (mousedown, so focus stays in the editor) — add an
  // empty shipment row to the open sales cell and focus its input.
  addSplit(event) {
    event.preventDefault()
    const container = event.currentTarget
      .closest("[data-sim-cell-target='group']")
      ?.querySelector("[data-sim-cell-parts]")
    const template = container?.querySelector("[data-sim-cell-part-row]")
    if (!template) return

    const row = template.cloneNode(true)
    const part = row.querySelector("[data-sim-cell-part]")
    part.value = ""
    container.appendChild(row)
    part.focus({ preventScroll: true })
  }

  // × on a shipment row (mousedown) — drop it, keeping at least one row. Focus
  // moves to a remaining input first so the removal never blurs the editor
  // (which would commit).
  removeSplit(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-sim-cell-part-row]")
    const rows = [...row.parentElement.querySelectorAll("[data-sim-cell-part-row]")]
    if (rows.length < 2) return

    const remaining = rows.filter((other) => other !== row)
    remaining.at(-1).querySelector("[data-sim-cell-part]").focus({ preventScroll: true })
    row.remove()
  }

  // Esc or the ✗ button — discard the in-progress edit, no submit.
  cancel(event) {
    if (event.type === "mousedown") event.preventDefault()
    sessionStorage.removeItem(this.#focusKey())
    const group = this.#openGroup()
    if (group) this.#close(group)
    this.openId = null
  }

  // Blur of the editing input — deferred so ✓/✗ (which keep focus via
  // preventDefault) and Enter/Esc win the race. Commits otherwise, except when
  // focus moves to another input of the same split editor.
  blur(event) {
    const group = event.target.closest("[data-sim-cell-target='group']")
    if (group && event.relatedTarget && group.contains(event.relatedTarget)) return

    queueMicrotask(() => {
      if (this.submitting || !this.openId) return
      this.#submitForm()
    })
  }

  // × on an added allocation row — remove its hidden field, any edited cells for
  // that row, and re-render.
  removeRow(event) {
    const label = event.currentTarget.dataset.simCellRemoveValue
    const param = event.currentTarget.dataset.simCellParamValue

    this.element
      .querySelectorAll(`input[type="hidden"][name="sim[rows][${param}][]"]`)
      .forEach((field) => {
        if (field.value === label) field.remove()
      })

    this.element
      .querySelectorAll(`input[name^="sim[${param}]["]`)
      .forEach((field) => {
        if (this.#fieldBelongsToRow(field, param, label)) field.remove()
      })

    this.submitting = true
    this.element.requestSubmit()
  }

  // --- private -------------------------------------------------------------

  #reveal(span) {
    const cell = span.closest("[data-sim-cell-target='cell']")
    const group = cell?.querySelector("[data-sim-cell-target='group']")
    const input = group?.querySelector("input[name]")
    if (!input) return

    const parts = group.querySelector("[data-sim-cell-parts]")
    if (parts) this.originalParts = parts.innerHTML
    const focusTarget = group.querySelector("[data-sim-cell-part]") || input

    // `invisible` (not `hidden`) so the value keeps reserving its width while the
    // overlay editor shows — the column never resizes, so there's no layout shift.
    span.classList.add("invisible")
    group.classList.remove("hidden")
    input.disabled = false
    focusTarget.focus({ preventScroll: true })
    focusTarget.select()
    this.openId = span.dataset.simCellId
  }

  #close(group) {
    const cell = group.closest("[data-sim-cell-target='cell']")
    const span = cell?.querySelector("[data-sim-cell-target='value']")
    const input = group.querySelector("input[name]")
    if (input) {
      input.value = input.dataset.original ?? input.value
      input.disabled = true
    }
    const parts = group.querySelector("[data-sim-cell-parts]")
    if (parts && this.originalParts !== undefined) parts.innerHTML = this.originalParts
    group.classList.add("hidden")
    span?.classList.remove("invisible")
  }

  #submitForm() {
    this.#joinOpenSplitParts()
    this.submitting = true
    this.element.requestSubmit()
  }

  // Blank parts drop out; clearing every part leaves the field blank, which
  // reverts the cell like clearing a single-value editor does.
  #joinOpenSplitParts() {
    const group = this.#openGroup()
    const parts = group?.querySelectorAll("[data-sim-cell-part]")
    const field = group?.querySelector("input[name]")
    if (!parts?.length || !field) return

    field.value = [...parts]
      .map((part) => part.value.trim())
      .filter((value) => value !== "")
      .join("+")
  }

  #openGroup() {
    return this.element.querySelector(
      "[data-sim-cell-target='group']:not(.hidden)"
    )
  }

  #fieldBelongsToRow(field, param, label) {
    return (
      field.name.startsWith(`sim[${param}][`) &&
      field.name.endsWith(`][${label}]`)
    )
  }

  #scopeSelectedSalesRowsToActiveCrop() {
    const cropLabel = this.#activeCropTypeLabel()
    if (!cropLabel) return

    this.element
      .querySelectorAll('input[name="sim[rows][s][]"]:checked')
      .forEach((field) => {
        if (field.value.includes(" / ")) return
        field.value = `${field.value} / ${cropLabel}`
      })
  }

  #focusFirstCellForAddedRow(submitter) {
    const param = submitter?.dataset.simCellParamValue
    const date = submitter?.dataset.simCellFirstDateValue
    if (!param || !date) return

    const checkedRow = this.element.querySelector(
      `input[name="sim[rows][${param}][]"]:checked`
    )
    if (!checkedRow) return

    sessionStorage.setItem(this.#focusKey(), `${param}|${date}|${checkedRow.value}`)
  }

  #activeCropTypeLabel() {
    const panel = this.element.querySelector('[role="tabpanel"][data-crop-type-label]:not(.hidden)')
    return panel?.dataset.cropTypeLabel
  }

  #reopenPendingCell() {
    const id = sessionStorage.getItem(this.#focusKey())
    if (!id) return
    sessionStorage.removeItem(this.#focusKey())
    const span = this.element.querySelector(`[data-sim-cell-id="${CSS.escape(id)}"]`)
    if (span) this.#reveal(span)
  }

  #restoreScroll() {
    this.scrollers = Array.from(this.element.querySelectorAll("[data-sim-cell-scroller]"))
    if (this.scrollers.length === 0) return

    const saved = this.#savedScrollPositions()
    this.scrollers.forEach((scroller, index) => {
      if (saved[index] !== undefined) {
        scroller.scrollLeft = saved[index]
      } else {
        this.#centerOnToday(scroller)
      }
    })

    this.scrollBindings = this.scrollers.map((scroller) => {
      const handler = this.#saveScroll.bind(this)
      scroller.addEventListener("scroll", handler, { passive: true })
      return { scroller, handler }
    })
  }

  #centerOnToday(scroller) {
    const column = scroller.querySelector("[data-sim-cell-today]")
    if (!column) return
    scroller.scrollLeft =
      column.offsetLeft - scroller.offsetWidth / 2 + column.offsetWidth / 2
  }

  #saveScroll() {
    sessionStorage.setItem(
      this.#scrollKey(),
      JSON.stringify(this.scrollers.map((scroller) => scroller.scrollLeft))
    )
  }

  #savedScrollPositions() {
    const saved = sessionStorage.getItem(this.#scrollKey())
    if (saved === null) return []

    try {
      const positions = JSON.parse(saved)
      if (Array.isArray(positions)) return positions.map((value) => parseFloat(value))
    } catch {
      // Fall through to the legacy single-value format below.
    }

    return [parseFloat(saved)]
  }

  #scrollKey() {
    return `sim-cell-scroll-${this.keyValue || "default"}`
  }

  #focusKey() {
    return `sim-cell-focus-${this.keyValue || "default"}`
  }
}
